import { useQuery } from '@tanstack/react-query';
import { authUtil } from 'src/utils';

export type CreditHistoryItem = {
  id: string;
  type: string;
  amount: number;
  feature?: string;
  date: string;
  meta?: { reason?: string };
};

export type CreditsResponse = {
  balance: number;
  // The account the server resolves credits for (owner-first). Purchases must
  // credit THIS account, not a client-derived one, or credits land elsewhere.
  accountId?: string;
  reportBalance?: number;
  resetDate?: string;
  monthlyCredits?: number;
  monthlyReports?: number;
  productInternalId?: string;
  period?: {
    start?: string;
    end?: string;
    granted?: number;
    consumed?: number;
  };
  history?: CreditHistoryItem[];
};

export type CreditPack = {
  internalId: string;
  credits: number;
  usd: number;
  // Paddle (Classic) one-time product id; absent where packs aren't configured
  // for the environment (e.g. sandbox) — the buy button stays disabled.
  paddleProductId?: string;
};

// Credit price per feature (1 credit = $0.10). The editorial report scales
// with length: max(minCredits, ceil(words / 10k) * creditsPer10kWords).
export type CreditFeaturePrice = {
  credits?: number;
  per?: string;
  creditsPer10kWords?: number;
  minCredits?: number;
};

export type CreditsConfig = {
  maxEditorWords?: number;
  features?: {
    editor_feedback?: CreditFeaturePrice;
    [key: string]: CreditFeaturePrice | undefined;
  };
  packs?: CreditPack[];
};

const apiBase = () =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}`;

export const useCredits = () => {
  return useQuery<CreditsResponse>(
    ['billingCredits'],
    async () => {
      const response = await fetch(`${apiBase()}/billing/credits`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'br-token': await authUtil.getFreshIdToken(),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch credits');
      }
      return response.json();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 30,
      retry: 1,
    }
  );
};

export const useCreditsConfig = () => {
  return useQuery<CreditsConfig>(
    ['billingCreditsConfig'],
    async () => {
      const response = await fetch(`${apiBase()}/billing/credits/config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'br-token': await authUtil.getFreshIdToken(),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch credits config');
      }
      return response.json();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      retry: 1,
    }
  );
};
